import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FilterDescriptor } from 'devextreme/data';
import DataSource from 'devextreme/data/data_source';
import { BehaviorSubject, Observable, Subscription, map, tap } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CommonsService } from './commons.service';
import { MessagesService } from './messages.service';
import { LoadResult, MachineActivity, MachineInfo } from 'src/api/generated/model/models';
import { MachineInfoService } from 'src/api/generated';

@Injectable({
  providedIn: 'root'
})
export class MachineInfoMainService {
  private _autoStatus: boolean = false;
  private _updateStatusInterval: any;

  public dataSource: DataSource<MachineInfo, string>;
  public _currentRow: BehaviorSubject<MachineInfo | undefined> = new BehaviorSubject<MachineInfo | undefined>(undefined);
  public currentRow$: Observable<MachineInfo | undefined> = this._currentRow.asObservable();
  public _actualMachine: BehaviorSubject<MachineInfo | undefined> = new BehaviorSubject<MachineInfo | undefined>(undefined);
  public actualMachine$: Observable<MachineInfo | undefined> = this._actualMachine.asObservable();
  public _currentMachine: BehaviorSubject<MachineInfo | undefined> = new BehaviorSubject<MachineInfo | undefined>(undefined);
  public currentMachine$: Observable<MachineInfo | undefined> = this._currentMachine.asObservable();
  public isDetail: boolean = false;
  private _currentMachineId: string | undefined;
  private _actualMachineId: string | undefined;

  // slave sources subscribe on this !!!!
  private _currentFilter: BehaviorSubject<FilterDescriptor | Array<FilterDescriptor>> = new BehaviorSubject([]);
  public currentFilter$: Observable<FilterDescriptor | Array<FilterDescriptor>> = this._currentFilter.asObservable();

  private _baseUrl: string = environment.baseUrl + "/api/MachineInfo";
  private _machActSrc: BehaviorSubject<MachineActivity[]> = new BehaviorSubject<MachineActivity[]>([]);
  public loadingAct: boolean = true;
  public machActSrc$: Observable<MachineActivity[]> = this._machActSrc.asObservable();

  public statusLoading: boolean = false;
  private subscription: Subscription | undefined;


  constructor(
    private _commonSvc: CommonsService,
    private _messSvc: MessagesService,
    protected _machineInfoSvc: MachineInfoService,
    private http: HttpClient,
  ) {
    this.dataSource = _commonSvc.makeAsyncDataSource<MachineInfo, string, MachineInfoService>(this._machineInfoSvc, this._actualMachine);
    this.currentRow$.subscribe({
      next: (r) => {
        this._currentFilter.next(["SourceId", "startswith", this._currentRow.value?.SourceId + "/"]);
      }
    });

    this.actualMachine$.subscribe({
      next: (r) => {
        this._currentFilter.next(["SourceId", "startswith", this._actualMachine.value?.SourceId + "/"]);
      }
    });


    this.currentMachine$.subscribe({
      next: (r) => {
        this._currentFilter.next(["SourceId", "startswith", this._currentMachine.value?.SourceId + "/"]);
      }
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  public set CurrentMachineId(id: string) {
    this._currentMachineId = id;
  }

  public get CurrentMachineId() {
    return this._currentMachineId ?? '-1';
  }

  public set CurrentMachine(r: MachineInfo | undefined) {
    if (r) {
      this._currentMachine.next(r);
      this._currentMachineId = r.Id;
    }
  }

  public get CurrentMachine(): MachineInfo | undefined {
    return this._currentMachine.value;
  }

  public set ActualMachineId(id: string) {
    this._actualMachineId = id;
  }

  public get ActualMachineId() {
    return this._actualMachineId ?? '-1';
  }

  public set ActualMachine(r: MachineInfo | undefined) {
    if (r) {
      this._actualMachine.next(r);
    }
  }

  public get ActualMachine(): MachineInfo | undefined {
    return this._actualMachine.value;
  }

  public set CurrentRowId(id: string) {
    if (this._currentRow.value?.Id != id) {
      this.dataSource.store().byKey(id).then((r) => { }); // do nothing just let it load record
    }
  }

  public get CurrentRow(): MachineInfo | undefined {
    return this._currentRow.value;
  }

  public set CurrentRow(r: MachineInfo) {
    this._currentRow.next(r);
  }

  public get AutoStatus(): boolean {
    return this._autoStatus;
  }

  public set AutoStatus(value: boolean) {
    this._autoStatus = value;
    if (this._autoStatus && this._actualMachine.value != undefined) {
      this._updateStatusInterval = setInterval(this.UpdateStatus.bind(this), 5000, this._actualMachine, this.http, this._baseUrl, this._messSvc);
    } else {
      clearInterval(this._updateStatusInterval);
    }
  }

  getAllMachines(): Observable<LoadResult> {
    return this._machineInfoSvc.getList().pipe(
      tap(machines => {
        this.setCurrentMachine(machines);
      })
    );
  }

  getAllMachinesOnly(): Observable<LoadResult> {
    return this._machineInfoSvc.getList().pipe();
  }

  getOneMachine(id: string): Observable<MachineInfo> {
    return this._machineInfoSvc.getOne(id);
  }

  private UpdateStatus(currentMachine: BehaviorSubject<MachineInfo>, http: HttpClient, baseUrl: string, mes: MessagesService) {
    if (this._actualMachine != undefined) {
      this.statusLoading = true;
      this._machineInfoSvc.getStatusMachineInfo(this._actualMachine.value?.Id ?? "-1").subscribe({
        next: (r) => {
          let mach = this._actualMachine.value;
          if (mach != undefined) {
            mach.CurrentStatus = r;
            currentMachine.next(mach);
          }
          this.statusLoading = false;
        },
        error: (e) => {
          this._messSvc.ShowError(JSON.stringify(e));
          console.error(e);
          this.statusLoading = false;
        }
      });

    }
  }

  public async delete(machineName: string) {
    await this._machineInfoSvc.softDelete(machineName);
  }

  public backUp() {
    // Must be created manually, because openapi-generator does not support returning httpResponses only the body. File name is in the header.
    return this.http.get(`${this._baseUrl}/backUpData/${this.ActualMachine?.Name ?? ""}`,
      {
        observe: 'response',
        responseType: 'blob' as 'json'
      }).pipe(
        map((res: any) => {
          if (res != null && res.body != null) {
            let tmp = res.headers.get('content-disposition');
            const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
            const matches = filenameRegex.exec(tmp);
            const filename = matches != null && matches[1] ? matches[1].replace(/['"]/g, '') : null;
            const data = {
              file: new Blob([res.body], { type: res.headers.get('Content-Type') ?? "application/zip" }),
              fileName: filename,
            }
            return data;
          }
          return null;
        })
      );
  }

  public restore(file: Blob, ownerName: string) {
    return this._machineInfoSvc.restoreDataMachineInfo(file, ownerName);
  }

  public moveToOtherOwner(newOwnerName: string) {
    return this._machineInfoSvc.moveToOtherOwnerMachineInfo(this.ActualMachine?.Name ?? "", newOwnerName);
  }

  public copy(newMachineName: string) {
    return this._machineInfoSvc.copyAllMachineDataMachineInfo(this.ActualMachine?.Name ?? "", newMachineName);
  }

  public move(newMachineName: string) {
    return this._machineInfoSvc.moveAllMachineDataMachineInfo(this.ActualMachine?.Name ?? "", newMachineName);
  }

  setCurrentMachine(machines: any) {
    if (machines.data) {
      this.ActualMachine = machines.data[0];
      this.CurrentMachine = machines.data[0];
    }
  }
}
